import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { HttpStatusCodes } from "@/lib/http-status-codes"
import { toast } from "sonner"
import { useCurrentOrganization } from "@/modules/organizations/hooks/use-organizations"
import {
  folderService,
  CreateFolderRequest,
  RenameFolderRequest,
} from "../infrastructure/folder-service"

export function useFolderList() {
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useQuery({
    queryKey: ["folders", "list", orgId],
    queryFn: () => folderService.listFolders(orgId),
    enabled: !!orgId,
  })
}

export function useFolderTree() {
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useQuery({
    queryKey: ["folders", "tree", orgId],
    queryFn: () => folderService.getFolderTree(orgId),
    enabled: !!orgId,
  })
}

export function useFolderContents(folderId: string | undefined) {
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useQuery({
    queryKey: ["folders", orgId, folderId, "contents"],
    queryFn: () => folderService.getFolderContents(orgId, folderId!),
    enabled: !!orgId && !!folderId,
  })
}

export function useCreateFolder() {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (data: CreateFolderRequest) =>
      folderService.createFolder(orgId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["folders", "tree", orgId] })
      toast.success("Folder created")
    },
    onError: () => {
      toast.error("Failed to create folder")
    },
  })
}

export function useRenameFolder() {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RenameFolderRequest }) =>
      folderService.renameFolder(orgId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["folders", "tree", orgId] })
      queryClient.invalidateQueries({ queryKey: ["folders", orgId] })
      toast.success("Folder renamed")
    },
    onError: () => {
      toast.error("Failed to rename folder")
    },
  })
}

export function useDeleteFolder() {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (id: string) => folderService.deleteFolder(orgId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["folders", "tree", orgId] })
      queryClient.invalidateQueries({ queryKey: ["documents", "all", orgId] })
      toast.success("Folder deleted")
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === HttpStatusCodes.BAD_REQUEST) {
        toast.error("Cannot delete: folder is not empty")
      } else {
        toast.error("Failed to delete folder")
      }
    },
  })
}
